import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import LoopHealth from './pages/LoopHealth';  // sprint-d9 G.5
import Devices from './pages/Devices';
import DeviceDetail from './pages/DeviceDetail';
import DeviceGroups from './pages/DeviceGroups';
import DeviceGroupDetail from './pages/DeviceGroupDetail';
import Drivers from './pages/Drivers';
import Vehicles from './pages/Vehicles';
import VehicleDetail from './pages/VehicleDetail';
import DriverDetail from './pages/DriverDetail';
import Licenses from './pages/Licenses';
import LicenseDetail from './pages/LicenseDetail';
import MediaScreens from './pages/MediaScreens/MediaScreens';
import MediaScreenDetail from './pages/MediaScreenDetail/MediaScreenDetail';
import GGOSDeviceList from './pages/GGOSDeviceList';
import GGOSDeviceDetail from './pages/GGOSDeviceDetail';
import IdentityList from './pages/IdentityList';
import IdentityDetail from './pages/IdentityDetail';
import BootstrapCAList from './pages/BootstrapCAList';
import BootstrapCADetail from './pages/BootstrapCADetail';
import BootstrapAuthLogs from './pages/BootstrapAuthLogs';
import DeviceEventLogs from './pages/DeviceEventLogs';
import Users from './pages/Users';
import UserDetail from './pages/UserDetail';
import AddUser from './pages/AddUser';
import TenantList from './pages/TenantList';
import TenantDetail from './pages/TenantDetail';
import BillingManagement from './pages/BillingManagement';
import BillingTransactions from './pages/BillingTransactions';
import BillingInvoices from './pages/BillingInvoices';
import BillingCredits from './pages/BillingCredits';
import BillingGateway from './pages/BillingGateway';
import MomentsKeyList from './pages/MomentsKeyList';
import MomentsKeyDetail from './pages/MomentsKeyDetail';
import MomentsBillingPlans from './pages/MomentsBillingPlans';
import AuditLog from './pages/AuditLog';
import Settings from './pages/Settings';
import CityList from './pages/CityList';
import CityDetail from './pages/CityDetail';
import SuperAdminMonitoring from './pages/SuperAdminMonitoring';
import ReleaseManagement from './pages/ReleaseManagement';
import SpeechLabelling from './pages/SpeechLabelling/SpeechLabelling';
import Campaigns from './pages/Campaigns/Campaigns';
import CampaignDetail from './pages/Campaigns/CampaignDetail';
import AuthGate from './auth/AuthGate';
import V2App from './v2/V2App';
import AtlasApp from './atlas/AtlasApp';
import './App.css';

// V2 mounts at /v2/* OUTSIDE <Layout>. Classic keeps its own shell and every
// route it has always had; the two coexist behind the same AuthGate and share
// only services/api.js. See v2/shell/nav.js for the porting map.
function App() {
  return (
    <Router>
      <AuthGate>
        <Routes>
          <Route path="/v2/*" element={<V2App />} />
          {/* Atlas — the founder's own account of the business. A third
              surface with its own information architecture and its own design
              system; see atlas/AtlasApp.js. */}
          <Route path="/atlas/*" element={<AtlasApp />} />
          <Route path="*" element={<ClassicApp />} />
        </Routes>
      </AuthGate>
    </Router>
  );
}

function ClassicApp() {
  return (
        <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/loop-health" element={<LoopHealth />} />
          <Route path="/devices" element={<Devices />} />
          <Route path="/devices/:deviceId" element={<DeviceDetail />} />
          <Route path="/device-groups" element={<DeviceGroups />} />
          <Route path="/device-groups/:groupId" element={<DeviceGroupDetail />} />
          <Route path="/drivers" element={<Drivers />} />
          <Route path="/drivers/:driverId" element={<DriverDetail />} />
          <Route path="/vehicles" element={<Vehicles />} />
          <Route path="/vehicles/:vehicleId" element={<VehicleDetail />} />
          <Route path="/licenses" element={<Licenses />} />
          <Route path="/licenses/:licenseId" element={<LicenseDetail />} />
          <Route path="/media-screens" element={<MediaScreens />} />
          <Route path="/media-screens/:thingName" element={<MediaScreenDetail />} />
          <Route path="/ggos-devices" element={<GGOSDeviceList />} />
          <Route path="/identities" element={<IdentityList />} />
          <Route path="/ggos-devices/identity/:unifiedId" element={<IdentityDetail />} />
          <Route path="/ggos-devices/:thingName" element={<GGOSDeviceDetail />} />
          <Route path="/speech-labelling" element={<SpeechLabelling />} />
          {/* Classic Moderation is retired (2026-09-15). Decisions live at
              /v2/demand and nowhere else. A redirect rather than a deletion
              because ClassicApp's inner <Routes> has no `*` and the server
              serves index.html for every path — a deleted route leaves a
              bookmarked /moderation as a blank page inside the chrome, which
              looks like an outage. Same idiom as / and /releases above. */}
          <Route path="/moderation" element={<Navigate to="/v2/demand" replace />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/campaigns/:id" element={<CampaignDetail />} />
          <Route path="/releases" element={<Navigate to="/releases/fleet" replace />} />
          <Route path="/releases/fleet" element={<ReleaseManagement tab="fleet" />} />
          <Route path="/releases/apks" element={<ReleaseManagement tab="apks" />} />
          <Route path="/releases/builds" element={<ReleaseManagement tab="builds" />} />
          <Route path="/bootstrap-cas" element={<BootstrapCAList />} />
          <Route path="/bootstrap-cas/:caId" element={<BootstrapCADetail />} />
          <Route path="/bootstrap-auth-logs" element={<BootstrapAuthLogs />} />
          <Route path="/device-event-logs" element={<DeviceEventLogs />} />
          <Route path="/tenants" element={<TenantList />} />
          <Route path="/tenants/:tenantId" element={<TenantDetail />} />
          <Route path="/billing" element={<BillingManagement />} />
          <Route path="/billing/accounts" element={<BillingManagement initialView="accounts" />} />
          <Route path="/billing/transactions" element={<BillingTransactions />} />
          <Route path="/billing/invoices" element={<BillingInvoices />} />
          <Route path="/billing/credits" element={<BillingCredits />} />
          <Route path="/billing/gateway" element={<BillingGateway />} />
          <Route path="/moments" element={<MomentsKeyList />} />
          <Route path="/moments/plans" element={<MomentsBillingPlans />} />
          <Route path="/moments/:keyId" element={<MomentsKeyDetail />} />
          <Route path="/users" element={<Users />} />
          <Route path="/users/:username" element={<UserDetail />} />
          <Route path="/add-user" element={<AddUser />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/cities" element={<CityList />} />
          <Route path="/cities/:cityId" element={<CityDetail />} />
          <Route path="/super-admin/monitoring" element={<SuperAdminMonitoring />} />
        </Routes>
        </Layout>
  );
}

export default App;
